import React, { useState } from 'react';
import { register } from '../api/api';
import { useTranslation } from '../i18n';

interface DesktopRegisterPageProps {
  onClose: () => void;
  onLoginClick: () => void;
}

const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 100 }, (_, i) => String(currentYear - 18 - i));

const DesktopRegisterPage: React.FC<DesktopRegisterPageProps> = ({ onClose, onLoginClick }) => {
  const { t } = useTranslation();
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({
        email,
        username,
        password,
        currency: 'EUR',
        country_code: 'DE',
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#f2f2f2] z-[200] flex flex-col overflow-y-auto">
      {/* Header */}
      <header className="sticky top-0 z-[100] shadow-md text-white h-[56px] flex-shrink-0 flex items-center px-6" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }}>
        <div className="w-full mx-auto flex items-center gap-10">
          <button onClick={onClose} className="header-alex-logo hover:opacity-80 transition cursor-pointer flex items-center">
            <img src="/tip24beth.svg" alt="Tip24" className="h-8 object-contain" />
          </button>
          <nav className="flex gap-1 h-full">
            {[
              { id: 'home', label: 'Sportwetten' },
              { id: 'live', label: 'Livewetten' },
              { id: 'games', label: 'Games' },
              { id: 'aktionen', label: 'Aktionen' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={onClose}
                className="text-[13px] font-bold h-[56px] px-4 transition uppercase tracking-wider flex items-center text-white/80 border-b-4 border-transparent hover:text-white hover:bg-tipico-red-dark"
              >
                {item.label}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-4">
            <button 
              type="button" 
              onClick={onLoginClick}
              className="text-white font-bold text-[15px] hover:opacity-80 transition"
            >
              Einloggen
            </button>
            <button 
              type="button" 
              className="bg-white text-tipico-red px-6 py-2 rounded-full text-[15px] font-bold shadow-md border-b-2 border-white"
            >
              Registrieren
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-10 px-4">
        <div className="bg-white w-full max-w-[960px] rounded-lg shadow-2xl overflow-hidden flex min-h-[740px]">
          
          {/* Left Side: Hero */}
          <div className="hidden md:flex md:w-[45%] relative overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80&w=1000" 
              alt="Bonus" 
              className="absolute inset-0 w-full h-full object-cover rounded-[4px]"
            />
            <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-10 pb-20">
              <div className="bg-tipico-red text-white text-[12px] font-black uppercase px-2 py-1 self-start mb-4 tracking-widest">
                Neukundenbonus
              </div>
              <h2 className="text-white text-4xl font-black leading-[1.1] tracking-tight">
                Verdopple deine erste Einzahlung <br/>
                <span className="text-white">bis zu 100 €</span>
              </h2>
              <p className="text-white/80 mt-6 text-sm font-bold uppercase tracking-wider">
                Jetzt registrieren ve bonusu kap!
              </p>
            </div>
          </div>

          {/* Right Side: Form */}
          <div className="w-full md:w-[55%] p-10 flex flex-col relative justify-center">
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-900 transition"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Step indicator */}
            <div className="flex gap-4 mb-8 self-end">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full border-2 border-black flex items-center justify-center text-[11px] font-black">1</div>
                <span className="text-[11px] font-black text-gray-900 uppercase tracking-widest">Persönliche Daten</span>
              </div>
              <div className="flex items-center gap-2 opacity-30">
                <div className="w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center text-[11px] font-black">2</div>
                <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Konto</span>
              </div>
            </div>

            <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Registrierung</h1>
            <p className="text-[13px] text-gray-500 mb-8 leading-snug">
              {t('auth.registrationOnlyGermany')} <br/>
              Felder mit <span className="text-tipico-red font-bold">*</span> sind Pflicht.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              
              {/* Name Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Vorname *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none"
                    required
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Nachname *</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none"
                    required
                  />
                </div>
              </div>

              {/* Birthdate Row */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Geburts­datum *</label>
                <div className="grid grid-cols-3 gap-3">
                  <select
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none appearance-none"
                    required
                  >
                    <option value="">Tag</option>
                    {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none appearance-none"
                    required
                  >
                    <option value="">Monat</option>
                    {MONTHS.map((m, i) => <option key={i} value={String(i+1).padStart(2, '0')}>{m}</option>)}
                  </select>
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none appearance-none"
                    required
                  >
                    <option value="">Jahr</option>
                    {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">E-Mail-Adresse *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none"
                  required
                />
              </div>

              {/* Username */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Benutzername *</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none"
                  required
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Passwort *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#f2f2f2] border-none rounded-md px-4 py-3 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-500 transition-shadow outline-none pr-12"
                    required
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900"
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && <p className="text-tipico-red text-[13px] font-bold">{error}</p>}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white font-black py-4 rounded-md text-sm uppercase tracking-widest hover:bg-gray-800 transition shadow-lg active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? 'Bitte warten...' : 'Weiter'}
              </button>

              {/* Login Link */}
              <p className="text-center text-[13px] font-bold text-gray-500 mt-2">
                Hast du bereits ein Konto?{' '}
                <button 
                  type="button" 
                  onClick={onLoginClick}
                  className="text-blue-600 hover:underline"
                >
                  Zum Login
                </button>
              </p>
            </form>
          </div>
        </div>
      </main>

      {/* Footer info */}
      <footer className="w-full max-w-[960px] mx-auto py-8 px-4 text-[11px] text-gray-400 leading-relaxed">
        <p>
          Tipico ist ein offiziell lizenzierter Sportwettenveranstalter, der unter der Aufsicht der Gemeinsamen Glücksspielbehörde der Länder steht. <br/>
          Glücksspiel kann süchtig machen. Spiele verantwortungsbewusst. Infos und Hilfe unter www.check-dein-spiel.de. 18+
        </p>
      </footer>
    </div>
  );
};

export default DesktopRegisterPage;
